import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPkr, type TripStatus } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { StatusPill } from '../components/StatusPill.js';
import { listTrips, type TripRow } from '../api/admin.js';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'searching', label: 'Searching' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'arriving', label: 'Arriving' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'in_progress', label: 'On trip' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function TripsPage() {
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');

  const trips = useQuery({
    queryKey: ['admin-trips', status],
    queryFn: () => listTrips({ status: status || undefined }),
    refetchInterval: 8000,
  });

  const filtered = useMemo(() => {
    if (!query) return trips.data ?? [];
    const q = query.toLowerCase();
    return (trips.data ?? []).filter(
      (t) =>
        t.rider.name.toLowerCase().includes(q) ||
        (t.driver?.user.name.toLowerCase().includes(q) ?? false) ||
        t.pickupLabel.toLowerCase().includes(q) ||
        t.dropLabel.toLowerCase().includes(q),
    );
  }, [trips.data, query]);

  const columns: Column<TripRow>[] = [
    {
      key: 'when',
      header: 'Requested',
      cell: (t) =>
        new Date(t.requestedAt).toLocaleString('en-PK', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    { key: 'rider', header: 'Rider', cell: (t) => <span className="font-medium">{t.rider.name}</span> },
    { key: 'driver', header: 'Driver', cell: (t) => t.driver?.user.name ?? <span className="text-slate-400">—</span> },
    { key: 'route', header: 'Route', cell: (t) => `${t.pickupLabel} → ${t.dropLabel}` },
    { key: 'vehicle', header: 'Vehicle', cell: (t) => <span className="capitalize">{t.vehicleType}</span> },
    { key: 'status', header: 'Status', cell: (t) => <StatusPill status={t.status as TripStatus} /> },
    {
      key: 'fare',
      header: 'Fare',
      align: 'right',
      cell: (t) => <span className="num font-semibold">{formatPkr(t.fareTotal)}</span>,
    },
  ];

  return (
    <Shell title="Trips" subtitle="Every ride on the platform.">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full max-w-md gap-2">
          <input
            type="search"
            className="input"
            placeholder="Search rider, driver or location"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select className="input max-w-[180px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(t) => t.id}
        loading={trips.isLoading}
        emptyMessage="No trips match these filters."
      />
    </Shell>
  );
}
